import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useLanguage from "../../hooks/useLanguage.js";
import {
  trackCTA,
  trackLead,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { buildSandboxUrl } from "../../lib/sandboxDemo.js";
import { WSE_SECTION } from "./sections.js";

/**
 * "A seating board you can open right now."
 *
 * The one framed, centred block on a page of left-aligned editorial sections —
 * the same `dwi-demo` treatment /digital-wedding-invitation, /wedding-planning
 * and /wedding-rsvp use, and for the same reason: on a page of statements, the
 * one thing that is an OFFER should not look like another paragraph. Reusing
 * those classes rather than minting a fifth identical block is also why this
 * page carries `dwi-page`.
 *
 * The offer is honest because the sandbox really does contain this feature:
 * `/try` mints a real `plan: "pro"` event and `seedSeating`
 * (api/services/sandbox-seed.service.mjs) creates two tables and spreads the
 * attending members across them, so the visitor lands on a populated board
 * rather than an empty one.
 *
 * TWO claim boundaries this section must never cross:
 *   1. Éros is blocked inside the sandbox (`blockSandboxAssistant` in
 *      api/routes/assistant.routes.mjs) and the seating page hides its "Ask
 *      Éros" button for a sandbox session. The note says so. A visitor who
 *      opens the demo expecting the assistant has been mis-sold by this
 *      section, not by the product.
 *   2. It is disposable, not an account. The event belongs to nobody and is
 *      swept on a TTL.
 *
 * `trackLead` fires alongside `trackCTA` here — unlike the invitation preview,
 * `/try` provisions a real (disposable) event, which is the site's own
 * definition of a lead and how the homepage pricing card already treats it.
 */
const SeatingDemo = () => {
  const { t } = useTranslation();
  const { shortLanguage } = useLanguage();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.DEMO, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="dwi-demo">
          <p className="wps-eyebrow">{t("weddingSeating.demo.eyebrow")}</p>
          <h2 className="heading-50">{t("weddingSeating.demo.heading")}</h2>
          <p className="body-20 dwi-demo-body">
            {t("weddingSeating.demo.body")}
          </p>
          <div className="dwi-demo-action">
            <Button
              href={buildSandboxUrl(shortLanguage)}
              target="_blank"
              rel="nofollow"
              text={t("weddingSeating.demo.cta")}
              className="button-lg"
              onClick={() => {
                const ctx = { ...analyticsContext, ctaName: "start_trial" };
                trackCTA("wedding_seating_demo", ctx);
                trackLead("wedding_seating_demo", ctx);
              }}
            />
          </div>
          <p className="wps-note dwi-demo-note">
            {t("weddingSeating.demo.note")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default SeatingDemo;
